import { ChatModChannel ,
         ChatModMessage ,
         ChatModBan     ,
         ChatModerationLevel ,
         CHAT_MODERATION_LEVELS } from '../../../dataTypes/moderationManagment/chat_types';

/**
 * Démonstration de l'écran Modération sans serveur : `?demoModeration` dans
 * l'adresse de la régie. Trois chaînes fictives reçoivent un message toutes les
 * secondes environ, notés au hasard et triés avec les mêmes seuils que
 * GoogleChatModerator. Sert à essayer l'écran, et à le vérifier tant que
 * l'API n'est pas déployée.
 */

export const isChatModerationDemo = (): boolean => {
  try { return new URLSearchParams(window.location.search).has('demoModeration'); }
  catch { return false; }
};

const PSEUDOS = [ 'Lina_92', 'MaxDuNord', 'camille.b', 'Yanis', 'Sophie L.', 'TomTom', 'Nour', 'Gégé75',
                  'Ana P.', 'leo_skate', 'Inès', 'KevinOff', 'Margaux', 'Bastien R.', 'zoé', 'Pti_Loup' ];

const SAFE = [
  "Trop bien ce direct 🔥", "On vous entend super bien maintenant", "Bonjour depuis Lyon !",
  "C'est quoi la musique derrière ?", "Bravo à toute l'équipe", "Première fois ici, j'adore",
  "Vous pouvez remontrer le plan large ?", "Salut tout le monde 👋", "Ça commence à quelle heure demain ?",
  "Le son coupe un peu chez moi", "Magnifique cette lumière", "Quelle ambiance !",
];

const DOUBTFUL = [
  { text: "Franchement c'est nul à chier ce passage",            cats: { Profanity: 0.71, Toxic: 0.38 } },
  { text: "Allez voir ma chaîne sur twitch.tv/promo_live",        cats: {}, flags: [ 'lien' ] },
  { text: "T'es vraiment con ou tu le fais exprès ?",             cats: { Insult: 0.66, Toxic: 0.58 } },
  { text: "Écris-moi au 06 12 34 56 78 pour la suite",            cats: {}, flags: [ 'coordonnees' ] },
  { text: "MAIS VOUS ÊTES SÉRIEUX LÀ ??? REMETTEZ LE SON",         cats: { Toxic: 0.22 }, flags: [ 'majuscules' ] },
  { text: "Ce politicien devrait aller en prison",                cats: { Politics: 0.82, Legal: 0.44, Toxic: 0.31 } },
  { text: "On va tous mourir avec ce réchauffement 😅",            cats: { 'Death, Harm & Tragedy': 0.48 } },
];

const TOXIC = [
  { text: "[message haineux envers une communauté]",              cats: { Derogatory: 0.93, Toxic: 0.91, Insult: 0.7 } },
  { text: "[menace explicite envers l'animateur]",                cats: { Violent: 0.96, Toxic: 0.88 } },
  { text: "[propos sexuels explicites]",                          cats: { Sexual: 0.97, Profanity: 0.6 } },
];

const WEIGHTS: Record<string, number> = {
  'Toxic': 1, 'Insult': 1, 'Derogatory': 1, 'Sexual': 1, 'Violent': 1,
  'Death, Harm & Tragedy': 0.8, 'Illicit Drugs': 0.8, 'Profanity': 0.75, 'Firearms & Weapons': 0.7, 'Public Safety': 0.6,
};

let nextId    = 5000;
let started   = false;
let channels  : ChatModChannel[] = [];
let messages  : ChatModMessage[] = [];
let bans      : ChatModBan[]     = [];

const pick = <T,>(list: T[]): T => list[Math.floor(Math.random() * list.length)];

const decide = (score: number | null, flags: string[], level: ChatModerationLevel): ChatModMessage['status'] => {
  const info = CHAT_MODERATION_LEVELS[level];
  if (info.publish === null || info.reject === null || score === null) return 'pending';
  if (score >= info.reject) return 'rejected';
  if (score < info.publish && !flags.includes('lien') && !flags.includes('coordonnees')) return 'approved';
  return 'pending';
};

const spawn = (channel: ChatModChannel, ageMs = 0) => {
  const roll = Math.random();
  const base = roll < 0.72 ? { text: pick(SAFE), cats: {} as Record<string, number> }
             : roll < 0.93 ? pick(DOUBTFUL)
             :               pick(TOXIC);
  const flags  = (base as { flags?: string[] }).flags ?? [];
  const cats   = { ...base.cats } as Record<string, number>;
  let   score  = Object.entries(cats).reduce((max, [name, c]) => Math.max(max, c * (WEIGHTS[name] ?? 0)), roll < 0.72 ? Math.random() * 0.18 : 0);
  if (flags.includes('majuscules')) score = Math.min(1, score + 0.1);
  const engineDown = Math.random() < 0.02;
  const status = decide(engineDown ? null : score, flags, channel.level);
  const now    = new Date(Date.now() - ageMs).toISOString();

  const pseudo = pick(PSEUDOS);

  messages.push({
    id: nextId++, channelId: channel.id, ownerId: 100 + PSEUDOS.indexOf(pseudo),
    pseudo, text: base.text, creationDate: now, status,
    aiScore: engineDown ? null : Math.round(score * 1000) / 1000, aiCategories: cats, aiFlags: flags,
    aiEngine: engineDown ? 'aucun' : 'google', aiReason: engineDown ? 'IA Google injoignable' : null,
    moderatedBy: status === 'pending' ? null : 'ai', moderationDate: status === 'pending' ? null : now,
    authorState: null, authorTotal24h: 1 + Math.floor(Math.random() * 6), authorRejected24h: status === 'rejected' ? 1 : 0,
  });
};

const start = () => {
  if (started) return;
  started  = true;
  channels = [
    { id: 18, name: 'Kleon TV',          publicRef: 'kleontv', slug: 'kleon-tv', hasLogo: false, active: true,  chatOpen: true,  level: 1, pending: 0, oldestPending: null, approvedHour: 0, rejectedHour: 0, byAiHour: 0, totalHour: 0, present: 214 },
    { id: 24, name: 'Concert Bercy',     publicRef: 'bercy',   slug: 'bercy',    hasLogo: false, active: true,  chatOpen: true,  level: 2, pending: 0, oldestPending: null, approvedHour: 0, rejectedHour: 0, byAiHour: 0, totalHour: 0, present: 1380 },
    { id: 31, name: 'Atelier cuisine',   publicRef: 'cuisine', slug: 'cuisine',  hasLogo: false, active: false, chatOpen: true,  level: 0, pending: 0, oldestPending: null, approvedHour: 0, rejectedHour: 0, byAiHour: 0, totalHour: 0, present: 12 },
    { id: 40, name: 'Coulisses (privé)', publicRef: null,      slug: null,       hasLogo: false, active: false, chatOpen: false, level: 3, pending: 0, oldestPending: null, approvedHour: 0, rejectedHour: 0, byAiHour: 0, totalHour: 0, present: 0 },
  ];
  bans = [ { id: 1, pseudo: 'troll_du_77', userId: 77, ip: '82.64.x.x', banDate: new Date(Date.now() - 3600e3).toISOString() } ];
  for (let i = 0; i < 70; i++) spawn(pick(channels.slice(0, 3)), Math.random() * 9 * 60e3);
  messages.sort((a, b) => a.id - b.id);
  window.setInterval(() => {
    const open = channels.filter(c => c.chatOpen);
    if (open.length > 0) spawn(pick(open.filter(c => c.active).concat(open)));
  }, 1300);
};

const refreshStats = () => {
  const hourAgo = Date.now() - 3600e3;
  channels = channels.map(c => {
    const mine    = messages.filter(m => m.channelId === c.id);
    const recent  = mine.filter(m => m.creationDate && Date.parse(m.creationDate) > hourAgo);
    const pending = mine.filter(m => m.status === 'pending');
    return { ...c,
      pending       : pending.length,
      oldestPending : pending[0]?.creationDate ?? null,
      approvedHour  : recent.filter(m => m.status === 'approved').length,
      rejectedHour  : recent.filter(m => m.status === 'rejected').length,
      byAiHour      : recent.filter(m => m.moderatedBy === 'ai').length,
      totalHour     : recent.length };
  });
};

const wait = () => new Promise(resolve => window.setTimeout(resolve, 120 + Math.random() * 160));

export const demoApi = {
  async channels(): Promise<ChatModChannel[]> {
    start(); await wait(); refreshStats();
    return channels.map(c => ({ ...c }));
  },
  async messages(channelIds: number[]) {
    start(); await wait();
    const mine = messages.filter(m => channelIds.includes(m.channelId));
    return {
      pending    : mine.filter(m => m.status === 'pending').map(m => ({ ...m })),
      recent     : mine.filter(m => m.status !== 'pending').slice(-200).reverse().map(m => ({ ...m })),
      serverTime : new Date().toISOString(),
    };
  },
  async decide(ids: number[], action: 'approve' | 'reject') {
    await wait();
    const now = new Date().toISOString();
    messages = messages.map(m => ids.includes(m.id)
      ? { ...m, status: action === 'approve' ? 'approved' : 'rejected', moderatedBy: 'admin', moderationDate: now } : m);
    return ids;
  },
  async sanction(messageId: number, action: 'kick' | 'ban') {
    await wait();
    const target = messages.find(m => m.id === messageId);
    if (!target) throw new Error('Message introuvable');
    let rejected = 0;
    messages = messages.map(m => {
      if (m.ownerId !== target.ownerId) return m;
      const wasPending = m.status === 'pending';
      if (wasPending) rejected++;
      return { ...m, authorState: action === 'ban' ? 'ban' : 'admin',
               ...(wasPending ? { status: 'rejected' as const, moderatedBy: 'admin' as const, moderationDate: new Date().toISOString() } : {}) };
    });
    if (action === 'ban') bans = [ { id: Date.now(), pseudo: target.pseudo, userId: target.ownerId, ip: '91.170.x.x', banDate: new Date().toISOString() }, ...bans ];
    return { pseudo: target.pseudo, closedConnections: 1, rejectedPending: rejected, banned: action === 'ban' };
  },
  async updateChannel(channelId: number, change: { chatOpen?: boolean; level?: ChatModerationLevel }) {
    await wait();
    channels = channels.map(c => c.id === channelId ? { ...c, ...change } : c);
    return { ok: true };
  },
  async bans() { await wait(); return bans.map(b => ({ ...b })); },
  async unban(banId: number) { await wait(); bans = bans.filter(b => b.id !== banId); return { ok: true }; },
};
